import c from 'classnames';
import { format } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { parseDate } from 'helpers';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import {
  useGetPractitionerNote,
  useUpsertPractitionerNote,
} from 'services/practitionerNote/usePractitionerNote';
import { getImpersonateId } from 'store/user';
import { KitType } from 'types/Kit';

import { TextEditor, Typography } from '@repo/ui';
import { Spinfinity } from 'components';

import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { PUBLISHED_PRACTITIONER_NOTE_QUERY_KEY } from 'services/practitionerNote/constants';
import usePractitionerNoteWarning from 'views/ActionPlan/PractitionerNote/hooks/usePractitionerNoteWarning';
import styles from './PatientNoteEditor.module.scss';

interface PatientNoteEditorProps {
  onClose: () => void;
}

export const PatientNoteEditor = ({ onClose }: PatientNoteEditorProps) => {
  const { trackEvent } = useAnalytics();
  const { currentKitId, currentKit, currentTinyAccount } =
    useCurrentKitAndTinyAccount();
  const practitionerId = useSelector(getImpersonateId);

  // Track the kit_id to persist it when navigating to routes without kit_id
  const persistedKitIdRef = useRef<string | null>(null);
  const [isSwitchingKits, setIsSwitchingKits] = useState(false);
  const switchStartTimeRef = useRef<number | null>(null);

  const { shouldShowWarning, toastWarning } = usePractitionerNoteWarning();

  const kitId = currentKitId || persistedKitIdRef.current;

  // Handle kit switching with minimum 2-second loading state
  useEffect(() => {
    if (!currentKitId) return;

    // Check if this is a kit switch (persistedKitIdRef already has a value and it's different)
    const isKitSwitch =
      persistedKitIdRef.current !== null &&
      persistedKitIdRef.current !== currentKitId;

    if (isKitSwitch) {
      setIsSwitchingKits(true);
      switchStartTimeRef.current = Date.now();
    }

    // Update persisted kit ID
    persistedKitIdRef.current = currentKitId;
  }, [currentKitId]);

  // Track panel opened event when component mounts with a kit_id
  useEffect(() => {
    if (kitId) {
      trackEvent('Prac Note Panel Opened', { kit_id: kitId });
    }
  }, [kitId, trackEvent]);

  const { data: practitionerNote, isLoading: isNoteLoading } =
    useGetPractitionerNote(kitId);

  const { mutate: upsertPractitionerNote, isPending: isUpsertPending } =
    useUpsertPractitionerNote(kitId);

  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isLoading = isNoteLoading || isUpsertPending;

  // Ensure minimum 2-second loading state when switching kits (to increase awareness)
  useEffect(() => {
    if (!isSwitchingKits || isNoteLoading) return;

    const elapsedTime = switchStartTimeRef.current
      ? Date.now() - switchStartTimeRef.current
      : 0;
    const remainingTime = Math.max(0, 2000 - elapsedTime);

    const timer = setTimeout(() => {
      setIsSwitchingKits(false);
      switchStartTimeRef.current = null;
    }, remainingTime);

    return () => clearTimeout(timer);
  }, [isSwitchingKits, isNoteLoading]);

  // Create unique localStorage key based on practitioner_id and kit_id
  const editorStateLocalStorageKey = useMemo(() => {
    if (!practitionerId || !kitId) return 'practitioner-note';
    return `practitioner-note-${practitionerId}-${kitId}`;
  }, [practitionerId, kitId]);

  const handleSubmit = (editorState: string) => {
    if (!kitId) {
      toast.error('No kit selected');
      return;
    }

    const noteExisted = !!practitionerNote?.id;

    // Track edited event if note already exists
    if (noteExisted) {
      trackEvent('Prac Note Edited', { kit_id: kitId });
    }

    upsertPractitionerNote(
      {
        content: editorState,
        is_published: true,
      },
      {
        onSuccess: data => {
          queryClient.setQueryData(
            [PUBLISHED_PRACTITIONER_NOTE_QUERY_KEY, kitId],
            data,
          );

          trackEvent('Prac Note Published', {
            kit_id: kitId,
            note_existed: noteExisted,
          });

          const toastMessage = `Practitioner note ${noteExisted ? 'updated' : 'created'} successfully. ${shouldShowWarning ? toastWarning : ''}`;

          toast.success(toastMessage);

          if (!pathname.includes('/action-plan'))
            navigate(`/action-plan/${kitId}`);
        },
        onError: error => {
          trackEvent('Prac Note Publish Failed', {
            kit_id: kitId,
            error: error instanceof Error ? error.message : 'Unknown error',
          });
          toast.error('Unable to publish. Please try again.');
        },
      },
    );
  };

  if (!kitId) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Typography variant='heading-m'>Note to the patient</Typography>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label='Close'
          >
            ✕
          </button>
        </div>
        <div className={(c(styles.description), 'mt-4')}>
          <Typography variant='body-s'>
            Please select a kit to add notes.
          </Typography>
        </div>
      </div>
    );
  }

  const patientName = currentTinyAccount?.first_name || 'Patient';
  const sampleDate = currentKit?.sample_date || '';
  const formattedDate = sampleDate
    ? format(parseDate(sampleDate), 'MM/dd/yy')
    : '';

  const testLabel =
    currentKit?.sampling_type === KitType.VAGINAL ? 'Vag Test' : 'Gut Test';
  const kitDescriptor = `${patientName}'s ${testLabel}${formattedDate ? ` (${formattedDate})` : ''}`;

  // Show kit switching state
  if (isSwitchingKits) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Typography variant='heading-m'>Note for Kit {kitId}</Typography>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label='Close'
          >
            ✕
          </button>
        </div>
        <div className={styles.switchingContainer}>
          <div className={styles.spinnerWrapper}>
            <Spinfinity />
          </div>
          <Typography variant='heading-m'>Switching kits</Typography>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Typography variant='heading-m'>Note for Kit {kitId}</Typography>
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label='Close'
        >
          ✕
        </button>
      </div>
      <div className={styles.subHeader}>
        <Typography variant='body-s'>{kitDescriptor}</Typography>
      </div>
      <div className={styles.description}>
        <Typography variant='body-s'>
          Your patient will see your notes on their{' '}
          <span className={styles.actionPlanHighlight}>Action Plan</span> page
          of the Patient Portal when their results are released.
        </Typography>
      </div>
      <div className={styles.editorWrapper}>
        {isNoteLoading ? (
          <div className={styles.loadingContainer}>
            <Spinfinity />
          </div>
        ) : (
          <TextEditor
            key={kitId}
            editorStateLocalStorageKey={editorStateLocalStorageKey}
            onSubmit={handleSubmit}
            onAutoSave={() =>
              trackEvent('Prac Note Draft Saved', { kit_id: kitId })
            }
            onRevert={() => trackEvent('Prac Note Reverted', { kit_id: kitId })}
            initialValue={practitionerNote?.content ?? undefined}
            disabled={isUpsertPending}
            isLoading={isUpsertPending}
            fullHeight
            buttonFullWidth
          />
        )}
      </div>
    </div>
  );
};

export default PatientNoteEditor;
